import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "../hooks/use-toast";
import { company } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  phone: "",
  email: "",
  service: "",
  message: "",
};

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service) {
      toast({
        title: "Missing information",
        description: "Please add your name, phone number and a service.",
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setForm(initialForm);
      toast({
        title: "Quote request sent!",
        description: "Thanks — the TNT Legacy team will reach out shortly.",
      });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Please try again or call us directly.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-slate-50 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left: info */}
          <div>
            <p className="font-cond uppercase tracking-[0.3em] text-[#0a6bb0] text-sm mb-3">
              Get In Touch
            </p>
            <h2 className="font-display text-4xl md:text-6xl text-[#071a2f] leading-tight">
              GET A QUOTE <span className="text-[#0aa5e0]">TODAY</span>
            </h2>
            <p className="text-slate-600 mt-5 text-lg max-w-md">
              Serving property managers and homeowners across {company.location}.
              Call, text, or send us a message — we respond fast.
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={company.phoneHref}
                className="flex items-center gap-4 group"
              >
                <span className="grid place-items-center h-14 w-14 rounded-full bg-[#071a2f] group-hover:bg-[#35c6f4] transition-colors">
                  <Phone className="h-6 w-6 text-[#35c6f4] group-hover:text-[#071a2f] transition-colors" />
                </span>
                <span>
                  <span className="block font-display text-2xl text-[#071a2f]">
                    {company.phone}
                  </span>
                  <span className="font-cond uppercase tracking-widest text-slate-500 text-xs">
                    Call or Text
                  </span>
                </span>
              </a>

              <a
                href={company.emailHref}
                className="flex items-center gap-4 group"
              >
                <span className="grid place-items-center h-14 w-14 rounded-full bg-[#071a2f] group-hover:bg-[#35c6f4] transition-colors">
                  <Mail className="h-6 w-6 text-[#35c6f4] group-hover:text-[#071a2f] transition-colors" />
                </span>
                <span>
                  <span className="block font-cond text-lg text-[#071a2f] break-all">
                    {company.email}
                  </span>
                  <span className="font-cond uppercase tracking-widest text-slate-500 text-xs">
                    Email Us
                  </span>
                </span>
              </a>

              <div className="flex items-center gap-4">
                <span className="grid place-items-center h-14 w-14 rounded-full bg-[#071a2f]">
                  <MapPin className="h-6 w-6 text-[#35c6f4]" />
                </span>
                <span>
                  <span className="block font-cond text-lg text-[#071a2f]">
                    {company.location}
                  </span>
                  <span className="font-cond uppercase tracking-widest text-slate-500 text-xs">
                    & Surrounding Areas
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="bg-white rounded-3xl shadow-xl shadow-black/10 p-8 md:p-10">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-[#071a2f] font-medium">
                    Name *
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Your name"
                    className="mt-2 h-12 border-slate-200 focus-visible:ring-[#35c6f4]"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#071a2f] font-medium">
                    Phone *
                  </Label>
                  <Input
                    id="phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="(727) 000-0000"
                    className="mt-2 h-12 border-slate-200 focus-visible:ring-[#35c6f4]"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-[#071a2f] font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@email.com"
                  className="mt-2 h-12 border-slate-200 focus-visible:ring-[#35c6f4]"
                />
              </div>

              <div>
                <Label className="text-[#071a2f] font-medium">
                  Service Needed *
                </Label>
                <Select
                  value={form.service}
                  onValueChange={(v) => update("service", v)}
                >
                  <SelectTrigger className="mt-2 h-12 border-slate-200 focus:ring-[#35c6f4]">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pool Services">Pool Services</SelectItem>
                    <SelectItem value="Lawn Services">Lawn Services</SelectItem>
                    <SelectItem value="Repair Services">
                      Repair Services
                    </SelectItem>
                    <SelectItem value="Multiple / Not Sure">
                      Multiple / Not Sure
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-[#071a2f] font-medium">
                  Message
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us about your property and what you need..."
                  rows={4}
                  className="mt-2 border-slate-200 focus-visible:ring-[#35c6f4] resize-none"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-13 py-6 bg-[#35c6f4] hover:bg-[#0a6bb0] text-[#071a2f] hover:text-white font-cond uppercase tracking-widest text-base rounded-full transition-colors"
              >
                {submitting ? (
                  "Sending..."
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Request My Quote
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
